import { useMemo, useState } from 'react'
import { ArrowLeft, Search, Send } from 'lucide-react'
import {
  FINANCE_AREA_META,
  type FinanceAreaKey,
  type PortalTypeKey,
} from '@/components/Financeiro/solicitacoes/financeSolicitacoesConfig'
import type { FinanceTypeCard } from '@/hooks/useFinanceiroSolicitacoesPage'
import { PortalPjAreaHubCard } from '@/components/PortalPj/PortalPjAreaHubCard'
import {
  formatTypeListSummary,
  PORTAL_HUB_ALL_TITLE,
  PORTAL_HUB_AREA_TITLES,
} from '@/components/PortalPj/portalPjAreaHub'
import {
  getPortalTypeCatalog,
  sortPortalCatalogTypes,
} from '@/components/PortalPj/portalPjTypeCatalog'
import { PortalPjAreaSelect } from '@/components/PortalPj/PortalPjAreaSelect'
import { PortalPjTypeList } from '@/components/PortalPj/PortalPjTypeList'

type HubScreen = { mode: 'hub' } | { mode: 'area'; area: FinanceAreaKey | 'ALL' }

type Props = {
  typesByArea: Map<FinanceAreaKey, FinanceTypeCard[]>
  allowedAreas: FinanceAreaKey[]
  onSelect: (key: PortalTypeKey) => void
}

export function PortalPjRequestHub({ typesByArea, allowedAreas, onSelect }: Props) {
  const [screen, setScreen] = useState<HubScreen>({ mode: 'hub' })
  const [query, setQuery] = useState('')

  const allItems = useMemo(
    () => allowedAreas.flatMap((area) => typesByArea.get(area) ?? []),
    [allowedAreas, typesByArea],
  )

  const searchResults = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return []
    return allItems.filter((item) => {
      const meta = getPortalTypeCatalog(item.key)
      const haystack = [
        item.label,
        item.key.replace(/_/g, ' '),
        meta.catalogTitle ?? '',
        meta.description,
      ]
        .join(' ')
        .toLowerCase()
      return haystack.includes(q)
    })
  }, [allItems, query])

  const isSearching = query.trim().length > 0

  if (screen.mode === 'area') {
    const items = sortPortalCatalogTypes(
      screen.area === 'ALL'
        ? allItems
        : (typesByArea.get(screen.area) ?? []),
      screen.area === 'ALL' ? undefined : screen.area,
    )

    return (
      <div className='space-y-5'>
        <button
          type='button'
          onClick={() => setScreen({ mode: 'hub' })}
          className='inline-flex items-center gap-2 text-sm font-medium text-accent hover:underline'
        >
          <ArrowLeft size={16} aria-hidden />
          Voltar às áreas
        </button>

        <PortalPjAreaSelect
          value={screen.area}
          allowedAreas={allowedAreas}
          showAllOption={allItems.length > 0}
          onChange={(area) => setScreen({ mode: 'area', area })}
        />

        <div>
          <h2 className='text-base font-semibold text-text-primary'>Como podemos ajudar você?</h2>
          {screen.area !== 'ALL' && (
            <p className='mt-1 text-sm text-text-muted'>
              {FINANCE_AREA_META[screen.area].description}
            </p>
          )}
        </div>

        <PortalPjTypeList items={items} onSelect={onSelect} catalog />
      </div>
    )
  }

  return (
    <div className='space-y-6'>
      <div className='relative'>
        <Search
          size={18}
          className='pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-text-muted'
          aria-hidden
        />
        <input
          id='portal-pj-search'
          type='search'
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder='Buscar solicitação (ex.: viagem, reembolso, freela…)'
          className='w-full rounded-xl border border-white/10 bg-white/[0.05] py-3 pl-10 pr-4 text-sm text-white shadow-sm transition-colors placeholder:text-white/40 focus:border-accent/50 focus:outline-none focus:ring-2 focus:ring-accent/20'
          aria-label='Buscar tipo de solicitação'
        />
      </div>

      {isSearching ? (
        <section aria-live='polite'>
          <h2 className='mb-3 text-sm font-semibold text-text-primary'>
            {searchResults.length > 0
              ? `${searchResults.length} resultado(s)`
              : 'Nenhum resultado'}
          </h2>
          {searchResults.length > 0 ? (
            <PortalPjTypeList items={searchResults} onSelect={onSelect} compact />
          ) : (
            <p className='text-sm text-text-muted'>
              Tente outro termo ou escolha uma área abaixo.
            </p>
          )}
        </section>
      ) : null}

      {!(isSearching && searchResults.length > 0) && (
      <div className='space-y-3'>
        <p className='flex items-center gap-2 text-sm font-medium text-text-primary'>
          <Send size={16} className='text-accent' aria-hidden />
          Entre em contato conosco a respeito de
        </p>

        <div className='space-y-3'>
          {allowedAreas.map((area) => {
            const items = typesByArea.get(area) ?? []
            if (items.length === 0) return null
            const summary = formatTypeListSummary(items.map((i) => i.label))
            return (
              <PortalPjAreaHubCard
                key={area}
                title={PORTAL_HUB_AREA_TITLES[area]}
                summary={summary}
                onClick={() => setScreen({ mode: 'area', area })}
              />
            )
          })}

          {allItems.length > 0 && (
            <PortalPjAreaHubCard
              title={PORTAL_HUB_ALL_TITLE}
              summary={formatTypeListSummary(allItems.map((i) => i.label), 12)}
              emphasized
              onClick={() => setScreen({ mode: 'area', area: 'ALL' })}
            />
          )}
        </div>
      </div>
      )}
    </div>
  )
}
